/**
 * Typed fetch wrapper + in-memory access-token store.
 *
 * Design:
 *   - Access token lives in module-local state, not localStorage — XSS safer.
 *     Accept the trade-off that a hard page refresh logs the user out until
 *     the refresh cookie re-issues one via `POST /auth/refresh`.
 *   - On a 401 with `code: "TOKEN_EXPIRED"`, we transparently call /refresh
 *     and retry the original request. Concurrent 401s share a single
 *     in-flight refresh promise (`sharedRefresh`) so a burst of expired
 *     requests results in exactly ONE network call to /auth/refresh. This
 *     matters once the backend enables strict refresh-token rotation, where
 *     two parallel refreshes would otherwise self-trigger family revocation.
 *   - If /refresh itself fails (e.g. the 30-day refresh cookie expired or
 *     was revoked), we clear the in-memory access token so the
 *     `AuthProvider` observes `user = null` on next probe and
 *     `<ProtectedRoute>` redirects to `/login`.
 *   - `sharedRefresh` is also exported so the AuthProvider can run a silent
 *     refresh on app boot without recursing through this 401-interceptor.
 *     The boot path has no access token at all, so the server returns plain
 *     `UNAUTHENTICATED` rather than `TOKEN_EXPIRED`, which would otherwise
 *     skip auto-refresh.
 *   - All non-ok responses throw an `ApiError` carrying the parsed body so
 *     calling code can distinguish e.g. RATE_LIMITED vs INVALID_CREDENTIALS.
 */

export const API_BASE = "/api/v1";

let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export interface ApiErrorBody {
  statusCode?: number;
  code?: string;
  message?: string;
  retryAfterSec?: number;
  [key: string]: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  readonly body: ApiErrorBody;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message ?? `HTTP ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.code = body.code;
    this.body = body;
  }
}

async function parseBody(res: Response): Promise<ApiErrorBody> {
  const text = await res.text();
  if (!text) return {};
  try {
    return JSON.parse(text) as ApiErrorBody;
  } catch {
    return { message: text };
  }
}

/**
 * Module-local in-flight refresh promise. While set, every caller that asks
 * for a refresh receives the same promise — guaranteeing that N concurrent
 * 401s produce exactly ONE `POST /auth/refresh` request. Cleared in the
 * `.finally()` of the underlying call so the next 401 (after this refresh
 * resolves, succeed or fail) starts a fresh refresh.
 */
let inFlightRefresh: Promise<boolean> | null = null;

async function performRefresh(): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: "POST",
      credentials: "include",
    });
    if (!res.ok) return false;
    const body = (await res.json()) as { accessToken?: string };
    if (!body.accessToken) return false;
    setAccessToken(body.accessToken);
    return true;
  } catch {
    return false;
  }
}

/**
 * Public refresh helper. Returns the in-flight promise if one is already
 * running, otherwise starts a new refresh. Used by both the 401 interceptor
 * below and the AuthProvider's boot effect (silent refresh on app load).
 *
 * Never throws — `false` covers every failure mode (network error, 401, no
 * accessToken in the body). Callers branch on the boolean.
 */
export function sharedRefresh(): Promise<boolean> {
  if (inFlightRefresh) return inFlightRefresh;
  const promise = performRefresh().finally(() => {
    inFlightRefresh = null;
  });
  inFlightRefresh = promise;
  return promise;
}

/**
 * Test-only helper to clear the module's in-flight refresh state. The
 * `inFlightRefresh` promise survives across `vi.unstubAllGlobals()` since
 * it lives in module scope, so a never-resolving fetch in one test will
 * wedge the next one. Tests call this in `afterEach` to break the cycle.
 *
 * Not part of the public surface; the leading `__` flags it as a test seam.
 */
export function __resetSharedRefreshForTests(): void {
  inFlightRefresh = null;
}

export interface ApiFetchInit extends RequestInit {
  /** Skip auto-refresh on 401 TOKEN_EXPIRED. Used internally by /auth/login. */
  skipRefresh?: boolean;
}

async function doFetch<T>(
  path: string,
  init: ApiFetchInit = {},
  attempt: number,
): Promise<T> {
  const headers = new Headers(init.headers ?? {});
  if (
    init.body !== undefined &&
    !headers.has("content-type") &&
    typeof init.body === "string"
  ) {
    headers.set("content-type", "application/json");
  }
  const token = getAccessToken();
  if (token && !headers.has("authorization")) {
    headers.set("authorization", `Bearer ${token}`);
  }

  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers,
    credentials: init.credentials ?? "include",
  });

  if (res.status === 401 && attempt === 0 && !init.skipRefresh) {
    const body = await parseBody(res);
    if (body.code === "TOKEN_EXPIRED") {
      const refreshed = await sharedRefresh();
      if (refreshed) {
        return doFetch<T>(path, init, 1);
      }
      // Silent refresh failed — the refresh session has ended.
      // Drop the in-memory access token so the next `/auth/me` probe
      // resolves as anonymous and ProtectedRoute bounces to /login.
      setAccessToken(null);
    }
    throw new ApiError(res.status, body);
  }

  if (!res.ok) {
    const body = await parseBody(res);
    throw new ApiError(res.status, body);
  }

  if (res.status === 204) {
    return undefined as T;
  }

  const ct = res.headers.get("content-type") ?? "";
  if (ct.includes("application/json")) {
    return (await res.json()) as T;
  }
  return (await res.text()) as unknown as T;
}

/**
 * Primary typed fetch helper. Prefixes `API_BASE`, injects the access token,
 * JSON-parses the response, and throws `ApiError` on non-2xx.
 */
export function apiFetch<T>(path: string, init?: ApiFetchInit): Promise<T> {
  return doFetch<T>(path, init, 0);
}
